import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, 
  TrendingUp, 
  Calendar, 
  BookOpen, 
  Target,
  Download,
  RefreshCw,
  Loader,
  AlertCircle,
  User
} from 'lucide-react';
import { generateStudentAnalysis } from '../../services/analysisEngine';
import CognitiveProfileCard from './CognitiveProfileCard';
import TestPerformanceChart from './TestPerformanceChart';
import BehavioralHeatmap from './BehavioralHeatmap';
import ProfileIntegrationPanel from './ProfileIntegrationPanel';
import AssignmentTimeline from './AssignmentTimeline';
import DoubtActivityChart from './DoubtActivityChart';
import RevisionEffectivenessGauge from './RevisionEffectivenessGauge';
import StudyStreakVisualization from './StudyStreakVisualization';
import TodoTaskDashboard from './TodoTaskDashboard';
import CourseProgressCards from './CourseProgressCards';
import CounsellingImpactChart from './CounsellingImpactChart';
import ProfileInsightsPanel from './ProfileInsightsPanel';
import BookmarksAnalysisCard from './BookmarksAnalysisCard';
import RecommendationCards from './RecommendationCards';
import TestBehaviorCard from './TestBehaviorCard';
import toast from 'react-hot-toast';

const StudentAnalysisDashboard = ({ userId, batchIds = [] }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loadingProgress, setLoadingProgress] = useState({ step: '', message: '' });
  const [timeRange, setTimeRange] = useState({
    start: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000), // Last 6 months
    end: new Date()
  });

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Target },
    { id: 'profile', name: 'Profile Integration', icon: User },
    { id: 'cognitive', name: 'Cognitive Profile', icon: Brain },
    { id: 'behavioral', name: 'Behavioral Patterns', icon: TrendingUp },
    { id: 'performance', name: 'Performance', icon: BookOpen },
    { id: 'recommendations', name: 'Recommendations', icon: Calendar }
  ];

  useEffect(() => {
    fetchAnalysisData();
  }, [userId, timeRange]);

  const fetchAnalysisData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Progressive callback to update UI as data loads
      const onProgress = (progress) => {
        setLoadingProgress(progress);
        
        // Update partial data as it becomes available
        if (progress.data) {
          setAnalysisData(prev => ({
            ...prev,
            ...progress.data
          }));
        }
      };

      const result = await generateStudentAnalysis(userId, batchIds, timeRange, onProgress);
      
      if (result.success) {
        setAnalysisData(result.data);
        setLoading(false);
      } else {
        setError(result.error || 'Failed to generate analysis');
        toast.error('Failed to load analysis data');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message);
      toast.error('An error occurred while loading analysis');
      setLoading(false);
    }
  };

  const refreshAnalysis = () => {
    fetchAnalysisData();
    toast.success('Refreshing analysis...');
  };

  const exportReport = async () => {
    toast.success('Export feature coming soon!');
  };

  if (loading && !analysisData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {loadingProgress.message || 'Analyzing your learning patterns...'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This may take a few moments
          </p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full text-center"
        >
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Unable to Load Analysis
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={refreshAnalysis}
            className="btn-primary inline-flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 shadow-md"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                MahStry Analysis
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Comprehensive insights into your cognitive and behavioral patterns
              </p>
              {loading && loadingProgress.message && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2 mt-2 text-sm text-blue-600 dark:text-blue-400"
                >
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>{loadingProgress.message}</span>
                </motion.div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={refreshAnalysis}
                disabled={loading}
                className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={exportReport}
                disabled={loading || !analysisData}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                Export Report
              </button>
            </div>
          </div>

          {/* Disclaimer Banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  Analysis Insights & Disclaimer
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  We are continuously improving our analysis algorithms for deeper and more accurate insights. 
                  The data presented here represents our current understanding based on your activity patterns. 
                  Please note that this analysis may not fully reflect your true potential or capabilities. 
                  Use these insights as guidance for improvement, not as definitive measures of your abilities.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Tabs */}
          <div className="mt-6 flex gap-2 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap inline-flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.name}
                </button>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <OverviewTab analysisData={analysisData} />
          )}
          {activeTab === 'profile' && (
            <ProfileTab analysisData={analysisData} />
          )}
          {activeTab === 'cognitive' && (
            <CognitiveTab analysisData={analysisData} />
          )}
          {activeTab === 'behavioral' && (
            <BehavioralTab analysisData={analysisData} />
          )}
          {activeTab === 'performance' && (
            <PerformanceTab analysisData={analysisData} />
          )}
          {activeTab === 'recommendations' && (
            <RecommendationsTab analysisData={analysisData} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ analysisData }) => {
  if (!analysisData) return null;
  
  return (
    <motion.div
      key="overview"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 20 }}
    className="space-y-6"
  >
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <CognitiveProfileCard cognitiveProfile={analysisData.cognitiveProfile} />
      <StudyStreakVisualization studyStreakData={analysisData.studyStreakAnalysis} />
    </div>
    
    <TestPerformanceChart testPerformance={analysisData.testPerformance} />
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <BehavioralHeatmap behavioralProfile={analysisData.behavioralProfile} />
      <RevisionEffectivenessGauge revisionAnalysis={analysisData.revisionAnalysis} />
    </div>

    <RecommendationCards 
      recommendations={analysisData.recommendations?.slice(0, 3) || []} 
      showAll={false}
    />
  </motion.div>
  );
};

// Profile Integration Tab Component
const ProfileTab = ({ analysisData }) => {
  if (!analysisData) return null;
  
  return (
    <motion.div
      key="profile"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="space-y-6"
    >
      <ProfileIntegrationPanel 
        cognitiveProfile={analysisData.cognitiveProfile}
        behavioralProfile={analysisData.behavioralProfile}
        profileSettings={analysisData.profileSettings}
      />
    </motion.div>
  );
};

// Cognitive Tab Component
const CognitiveTab = ({ analysisData }) => {
  if (!analysisData) return null;
  
  return (
  <motion.div
    key="cognitive"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 20 }}
    className="space-y-6"
  >
    <CognitiveProfileCard cognitiveProfile={analysisData.cognitiveProfile} expanded />
    <TestPerformanceChart testPerformance={analysisData.testPerformance} />
    <RevisionEffectivenessGauge revisionAnalysis={analysisData.revisionAnalysis} />
    <BookmarksAnalysisCard bookmarksAnalysis={analysisData.bookmarksAnalysis} />
  </motion.div>
  );
};

// Behavioral Tab Component
const BehavioralTab = ({ analysisData }) => {
  if (!analysisData) return null;
  
  return (
    <motion.div
    key="behavioral"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 20 }}
    className="space-y-6"
  >
    <BehavioralHeatmap behavioralProfile={analysisData.behavioralProfile} />
    
    {/* NEW: Detailed Test Behavior Analysis */}
    <TestBehaviorCard testBehaviorAnalysis={analysisData.testBehaviorAnalysis} />
    
    <StudyStreakVisualization studyStreakData={analysisData.studyStreakAnalysis} />
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <TodoTaskDashboard todoTaskAnalysis={analysisData.todoTaskAnalysis} />
      <DoubtActivityChart doubtAnalysis={analysisData.doubtAnalysis} />
    </div>

    <AssignmentTimeline assignmentAnalysis={analysisData.assignmentAnalysis} />
    <ProfileInsightsPanel 
      profileSettings={analysisData.profileSettings}
      behavioralProfile={analysisData.behavioralProfile}
      cognitiveProfile={analysisData.cognitiveProfile}
    />
  </motion.div>
  );
};

// Performance Tab Component
const PerformanceTab = ({ analysisData }) => {
  if (!analysisData) return null;
  
  return (
    <motion.div
    key="performance"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 20 }}
    className="space-y-6"
  >
    <TestPerformanceChart testPerformance={analysisData.testPerformance} />
    <AssignmentTimeline assignmentAnalysis={analysisData.assignmentAnalysis} />
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <DoubtActivityChart doubtAnalysis={analysisData.doubtAnalysis} />
      <RevisionEffectivenessGauge revisionAnalysis={analysisData.revisionAnalysis} />
    </div>

    <CourseProgressCards courseEnrollmentAnalysis={analysisData.courseEnrollmentAnalysis} />
    
    {analysisData.counsellingAnalysis?.totalSessions > 0 && (
      <CounsellingImpactChart counsellingAnalysis={analysisData.counsellingAnalysis} />
    )}
  </motion.div>
  );
};

// Recommendations Tab Component
const RecommendationsTab = ({ analysisData }) => {
  if (!analysisData) return null;
  
  return (
    <motion.div
    key="recommendations"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 20 }}
    className="space-y-6"
  >
    <RecommendationCards 
      recommendations={analysisData.recommendations} 
      showAll={true}
    />
  </motion.div>
  );
};

export default StudentAnalysisDashboard;
